import { type HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { Placeholder, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useInView } from 'react-intersection-observer';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { shouldShowStrikeThroughPrice } from '@/app/components/hotel-detail/rooms/roomCommon';
import { StrikeThroughPrice } from '@/app/components/hotel-detail/common/StrikeThroughPrice';
import { useStickyHeaderOffset } from '@/app/components/hotel-detail/hooks/useStickyHeaderOffset.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import { useTranslation } from '../../translations/Translation';
import { useOffersContext } from '../OffersProvider';
import { ExcludedChargesLabelContainer, LocalTaxes, StyledPriceTypeContainer } from '../rooms/LocalTaxes';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import customCss from '../css/customCss';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';

// #region styled components
const HotelName = styled(Text).attrs({ variant: 'BodySmall', weight: 'bold' })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;

  @media ${customCss.breakpoints.lg} {
    white-space: normal;
    ${(p) => p.theme.kallisto.mixins.font('Body')};
    font-weight: ${(p) => p.theme.kallisto.font.weight.bold};
  }
`;

const RoomName = styled(Text).attrs({ variant: 'Caption' })`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;

  @media ${customCss.breakpoints.lg} {
    white-space: normal;
    ${(p) => p.theme.kallisto.mixins.font('BodySmall')};
  }
`;

const Price = styled(Text).attrs({ variant: 'Title3', forceTag: 'span' })``;

const PriceType = styled(Text).attrs({ variant: 'Caption', forceTag: 'span' })`
  margin-left: 4px;
`;

// #endregion

type AccommadationCardProps = {
  sticky?: boolean;
} & HTMLAttributes<HTMLElement>;

export const AccommodationCard = (props: AccommadationCardProps) => {
  const { sticky = true, ...rest } = props;
  const { data: hotelStaticData } = useFetchHotelStatic();

  const { selectedRoom, selectedMealPlan } = useSelectedMealPlan();
  const { stickyTop, stickyTopCss } = useStickyHeaderOffset();

  const [ref, notOnTop] = useInView({ threshold: 0, triggerOnce: false, rootMargin: `-${stickyTop}px 0px 0px 0px` });
  const fixedOnTop = !notOnTop;

  return selectedMealPlan ? (
    <>
      {/* On IOS we need to use another anchor to know if the component is its sticky position, because the sticky element is changing by small pixels his top position while scrolling */}
      <div
        ref={ref}
        className="sticky-anchor"
        css={`
          height: 0;
          margin-top: 16px;
          @media ${customCss.breakpoints.lg} {
            margin-top: 0;
          }
        `}
      />
      <div
        className="accommodation-card"
        css={css`
          background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};

          ${
            sticky
              ? css`
                  position: sticky;
                  ${stickyTopCss};
                  z-index: 1;
                  border-radius: ${fixedOnTop ? '0' : '8px'};
                  transition: all 0.2s ease-in-out;
                  width: ${fixedOnTop ? '100%' : 'calc(100% - 32px)'};
                  margin: ${fixedOnTop ? '0' : '0 auto'};
                  box-shadow: ${fixedOnTop ? '0px 4px 8px 0px #0000000a' : 'none'};

                  @media ${customCss.breakpoints.sm} {
                    width: ${fixedOnTop ? '100%' : 'calc(100% - 48px)'};
                  }

                  @media ${customCss.breakpoints.lg} {
                    width: 100%;
                    ${fixedOnTop ? 'border-radius: 0 0 8px 8px;' : undefined};
                    //  Adjust margin to account for the sticky anchor (The parent has a 16px row gap)
                    margin: -16px 0 0 0;
                  }
                `
              : css`
                  border-radius: 8px;
                `
          }
        `}
      >
        <AccommadationSection
          hotelName={hotelStaticData?.name || ''}
          roomName={selectedRoom?.name || ''}
          mealPlan={selectedMealPlan}
        />
      </div>
    </>
  ) : (
    <AccommodationCardSkeleton {...rest} />
  );
};

type AccommadationSectionProps = {
  hotelName: string;
  roomName: string;
  mealPlan: MealPlanType;
} & HTMLAttributes<HTMLElement>;

const AccommadationSection = (props: AccommadationSectionProps) => {
  const { hotelName, roomName, mealPlan } = props;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
        color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
        padding: 8px 16px;
        gap: 4px;
      `}
    >
      <AccommadationInfo hotelName={hotelName} roomName={roomName} />
      <PriceSection mealPlan={mealPlan} />
    </div>
  );
};

type AccommadationInfoProps = {
  hotelName: string;
  roomName?: string;
} & HTMLAttributes<HTMLElement>;

const AccommadationInfo = (props: AccommadationInfoProps) => {
  const { hotelName, roomName } = props;
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        align-self: center;
        gap: 4px;
        flex: 1;
        min-width: 50%;
      `}
    >
      <HotelName>{hotelName}</HotelName>
      <RoomName>{roomName}</RoomName>
    </div>
  );
};

type PriceContainerProps = {
  mealPlan?: MealPlanType;
} & HTMLAttributes<HTMLElement>;

const PriceSection = (props: PriceContainerProps) => {
  const { mealPlan } = props;

  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const { offersData } = useOffersContext();
  const { totalGuestsWithoutInfants, accommodationOnly } = useUrlSearchParams();

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-end;
      `}
    >
      <div>
        {mealPlan?.strikeThroughPrice && shouldShowStrikeThroughPrice(mealPlan, accommodationOnly) ? (
          <StrikeThroughPrice
            css={`
              margin-right: 8px;
            `}
          >
            {formatPrice({ price: mealPlan.strikeThroughPrice, localization })}
          </StrikeThroughPrice>
        ) : null}
        <Price>{mealPlan ? formatPrice({ price: mealPlan.finalPrice, localization }) : null}</Price>
        <PriceType>{t('vacation.select.room.price.dp.perperson.short')}</PriceType>
      </div>

      <LocalTaxes
        nights={offersData?.nights}
        passengers={totalGuestsWithoutInfants}
        roomsIncludedInPrice={offersData?.roomsIncludedInPriceForCheapestRoom || 1}
        excludedCharges={mealPlan?.excludedCharges}
        excludedChargesIncluded={mealPlan?.excludedChargesIncluded}
        price={mealPlan?.finalTotalPrice || 0}
        priceWithoutTaxes={mealPlan?.priceWithoutTaxes}
        showTotalPriceIncludedTaxes={true}
        css={`
          ${ExcludedChargesLabelContainer} {
            text-align: right;
          }
          ${StyledPriceTypeContainer} {
            white-space: normal;
            text-align: right;
          }
        `}
      />
    </div>
  );
};

const AccommadationSectionSkeleton = () => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
      padding: 10px 16px;
      gap: 4px;
    `}
  >
    <Placeholder height="14px" width="60%" />
    <div
      css={`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-end;
      `}
    >
      <Placeholder height="14px" width="40%" />
      <Placeholder height="18px" width="20%" />
    </div>
  </div>
);

const AccommodationCardSkeleton = (props: AccommadationCardProps) => (
  <div
    {...props}
    css={css`
      background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
      border-radius: 8px;
      margin: 0 16px;

      @media ${customCss.breakpoints.sm} {
        margin: 0 24px;
      }

      @media ${customCss.breakpoints.lg} {
        margin: 0;
      }
    `}
  >
    <AccommadationSectionSkeleton />
  </div>
);
